import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const legendQuestions = [
  {
    question: "Où étais-tu avant ton aventure entrepreneuriale ? Quel était ton \"monde ordinaire\" ?",
    placeholder: "Décris ta situation, ton job, ton quotidien, ce qui te manquait...",
    tip: "Donne 2-3 repères concrets (job/quotidien, contexte perso, ce qui te manquait). Plus c'est spécifique, plus c'est crédible et mémorable."
  },
  {
    question: "Quel événement, prise de conscience ou déclic t'a poussé·e à te lancer ?",
    placeholder: "Un événement, une rencontre, une réalisation soudaine...",
    tip: "Ce moment clé où tout a basculé - c'est souvent le cœur de ton histoire."
  },
  {
    question: "As-tu résisté à l'appel ? Quelles peurs t'ont ralenti au départ ?",
    placeholder: "Doutes, peurs, hésitations, ce qui t'a freiné...",
    tip: "Ton audience a les mêmes peurs - en les partageant, tu crées une connexion authentique."
  },
  {
    question: "Qui t'a inspiré·e, aidé·e ou guidé·e à ce moment-là ?",
    placeholder: "Un mentor, un livre, un coach, une rencontre...",
    tip: "Le guide/mentor dans le voyage du héros - ça peut être une personne ou même un livre."
  },
  {
    question: "Quelle a été ta première décision ou action forte pour te lancer ?",
    placeholder: "Ta première action concrète, ce premier pas...",
    tip: "Le moment où tu es passé·e à l'action - c'est inspirant pour ton audience."
  },
  {
    question: "Quelles épreuves, obstacles ou adversaires as-tu rencontrés ? Qui t'a soutenu ?",
    placeholder: "Difficultés, échecs, personnes qui t'ont mis des bâtons dans les roues...",
    tip: "Les épreuves rendent ton histoire crédible et ton succès mérité."
  },
  {
    question: "Y a-t-il eu un moment de creux ou de crise ? Qu'est-ce que tu as ressenti à ce moment-là ?",
    placeholder: "Le moment le plus dur, tes émotions à ce moment...",
    tip: "La \"caverne profonde\" du voyage du héros - le moment où tout semblait perdu."
  },
  {
    question: "Quel défi majeur as-tu dû relever ? Comment as-tu trouvé la force de le surmonter ?",
    placeholder: "L'épreuve ultime et comment tu l'as surmontée...",
    tip: "C'est ta transformation - ce qui fait de toi un expert légitime."
  },
  {
    question: "Qu'as-tu gagné, compris ou transformé grâce à cette épreuve ?",
    placeholder: "Les leçons, la sagesse acquise, ce qui a changé en toi...",
    tip: "L'élixir que tu ramènes de ton voyage - c'est ce que tu transmets maintenant."
  },
  {
    question: "Comment cette expérience a-t-elle changé ton business ou ta mission ?",
    placeholder: "L'impact sur ton activité, ta vision, ton pourquoi...",
    tip: "Le lien entre ton histoire personnelle et ta mission professionnelle."
  },
  {
    question: "Quelle nouvelle version de toi est née de cette traversée ?",
    placeholder: "Qui es-tu devenu·e ? En quoi es-tu différent·e ?",
    tip: "Ta transformation personnelle - c'est ce qui inspire ton audience."
  },
  {
    question: "Quelle sagesse, valeur ou leçon profonde transmets-tu aujourd'hui à ton audience ?",
    placeholder: "Le message clé que tu veux partager, ta philosophie...",
    tip: "C'est le cœur de ta communication - ce qui te rend unique."
  }
];

const Legend = () => {
  const { studyId } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState(Array(legendQuestions.length).fill(''));
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [storytelling, setStorytelling] = useState(null);
  const navigate = useNavigate();
  
  // Mode bonus = pas de studyId
  const isBonusMode = !studyId;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
    }
  }, [navigate]);

  const progress = ((currentStep + 1) / legendQuestions.length) * 100;

  const handleNext = () => {
    if (!responses[currentStep].trim()) {
      toast.error('Veuillez répondre à la question');
      return;
    }
    if (currentStep < legendQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!responses[currentStep].trim()) {
      toast.error('Veuillez répondre à la question');
      return;
    }

    setGenerating(true);
    const token = localStorage.getItem('token');

    try {
      const payload = {
        responses: legendQuestions.map((q, i) => ({
          question: q.question,
          answer: responses[i]
        }))
      };

      let response;
      if (isBonusMode) {
        // Mode bonus - endpoint sans study_id
        response = await axios.post(
          `${API}/legend/generate`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStorytelling(response.data.storytelling);
        toast.success('Ta légende a été créée !');
      } else {
        // Mode étude - endpoint avec study_id
        response = await axios.post(
          `${API}/studies/${studyId}/legend`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Ta légende a été créée !');
        navigate(`/study/${studyId}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la création de la légende');
    } finally {
      setGenerating(false);
    }
  };

  const currentQuestion = legendQuestions[currentStep];

  // Si on a le storytelling généré (mode bonus), afficher le résultat
  if (storytelling) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="border-b border-border bg-background/95 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-heading font-semibold text-primary">
                  ✨ Ta Légende Personnelle
                </h1>
                <p className="text-sm text-muted">Voyage du Héros - Terminé !</p>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="text-muted hover:text-foreground"
              >
                Retour au tableau de bord
              </Button>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-xl p-8 prose prose-invert max-w-none">
              <div dangerouslySetInnerHTML={{ __html: storytelling.replace(/\n/g, '<br/>') }} />
            </div>
            <div className="flex justify-center gap-4 mt-6">
              <Button 
                variant="outline"
                onClick={() => {
                  setStorytelling(null);
                  setCurrentStep(0);
                  setResponses(Array(legendQuestions.length).fill(''));
                }}
              >
                Recommencer
              </Button>
              <Button onClick={() => navigate('/dashboard')} className="shadow-glow-md">
                Retour au tableau de bord
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-heading font-semibold text-primary">
                ✨ {isBonusMode ? 'Bonus : ' : ''}Ta Légende Personnelle
              </h1>
              <p className="text-sm text-muted">Voyage du Héros - Question {currentStep + 1}/{legendQuestions.length}</p>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate(isBonusMode ? '/dashboard' : `/study/${studyId}`)}
              className="text-muted hover:text-foreground"
            >
              {isBonusMode ? 'Retour au tableau de bord' : 'Retour à l\'étude'}
            </Button>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-2xl"
        >
          <div className="bg-card border border-border rounded-xl p-8">
            {/* Question */}
            <h2 className="text-lg font-medium mb-2">
              {currentQuestion.question}
            </h2>
            
            {/* Tip */}
            <p className="text-sm text-muted mb-6 bg-muted/20 p-3 rounded-lg">
              💡 {currentQuestion.tip}
            </p>

            {/* Input */}
            <Textarea
              value={responses[currentStep]}
              onChange={(e) => {
                const newResponses = [...responses];
                newResponses[currentStep] = e.target.value;
                setResponses(newResponses);
              }}
              placeholder={currentQuestion.placeholder}
              className="min-h-[150px] text-base"
              disabled={generating}
              data-testid="legend-textarea"
            />

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0 || generating}
                data-testid="legend-prev-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>

              {currentStep < legendQuestions.length - 1 ? (
                <Button onClick={handleNext} disabled={generating} data-testid="legend-next-btn">
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={generating}
                  className="bg-primary shadow-glow-md"
                  data-testid="legend-submit-btn"
                >
                  {generating ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Créer ma légende
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Progress indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {legendQuestions.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (index < currentStep || responses[index]) {
                    setCurrentStep(index);
                  }
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-primary scale-125'
                    : index < currentStep || responses[index]
                    ? 'bg-primary/50 hover:bg-primary/70'
                    : 'bg-muted'
                }`}
                disabled={generating}
              />
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Legend;
